import {
  CmpEventAttendanceModeEnum,
  CmpEventCustomFieldInput,
  CmpEventCustomFieldReturn,
  CmpEventCustomFieldScopeEnum,
  CmpEventCustomFieldTypeEnum,
  CmpEventDataInput,
  CmpEventKindEnum,
  CmpEventSponsorshipTaxModeEnum,
  CmpEventDressCodeEnum,
  CmpEventFieldsFragment,
  CmpEventLocationModeEnum,
  CmpEventStatusEnum,
  CmpEventTicketAudienceEnum,
  CmpEventVisibilityEnum,
  GetCmpEventTypesQuery,
} from "../../../graphql/types";
import {
  EventCustomField,
  EventFormState,
  EventRegistrationQuestion,
  EventStatus,
  EventTypeOption,
  EventVisibility,
  createBlankEventFormState,
  createDefaultRegistrationQuestions,
} from "./eventMockData";
import {
  normalizeCmpTimeZone,
  utcMilliToDateTimeInputParts,
} from "../../../utils/cmpTimeZones";
import { eventDateTimeToUtcMilli as dateTimeInputPartsToUtcMilli } from "./eventDateTime";
import { centsToCurrencyValue } from "../../common/dashboard/currencyUnits";
import { resolveEventSpeakerKeynotes } from "./eventSpeakerKeynote";
import { withBreakableEmailSpaces } from "../MyChamber/ChamberSettings/tabs/automatedEmailSpaces";

type EventTypeReturn =
  GetCmpEventTypesQuery["getCmpEventTypes"]["eventTypes"][number];

const parseCents = (value: string): number => {
  const normalizedValue = value.replace(/[^0-9.-]/g, "");
  const parsedValue = Number(normalizedValue || "0");

  return Number.isFinite(parsedValue) ? Math.round(parsedValue * 100) : 0;
};

const formatCents = (value?: number | null) =>
  `$${((value || 0) / 100).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const parseOptionalNumber = (value: string): number | null => {
  const parsedValue = Number(value);

  return value.trim() && Number.isFinite(parsedValue) ? parsedValue : null;
};

const FREE_EVENT_TICKET_ID = "free-general-admission";
const FREE_EVENT_TICKET_CODE = "FREE";
const FREE_EVENT_TICKET_NAME = "General Admission";
const FREE_EVENT_TICKET_DESCRIPTION = "Free event registration.";
const INTERNAL_FREE_TICKET_IDS = new Set([
  "preloaded-ticket-sponsor-no-charge",
  "preloaded-ticket-complimentary",
]);
const INTERNAL_FREE_TICKET_SIGNATURES = new Map([
  ["NCST", "No charge tickets allocated with sponsorship packages"],
  [
    "CT",
    "Tickets that are assigned to attendees at no charge and for internal use only",
  ],
]);

const isInternalFreeTicketSeed = (ticket: {
  id: string;
  code?: string | null;
  description?: string | null;
}) => {
  if (INTERNAL_FREE_TICKET_IDS.has(ticket.id)) {
    return true;
  }

  const normalizedCode = ticket.code?.trim().toUpperCase() || "";
  const defaultDescription =
    INTERNAL_FREE_TICKET_SIGNATURES.get(normalizedCode);

  return (
    !!defaultDescription && ticket.description?.trim() === defaultDescription
  );
};

const dateInputToUtcMilli = ({
  boundary,
  timeZone,
  value,
}: {
  boundary: "start" | "end";
  timeZone: string;
  value?: string;
}): number | undefined => {
  if (!value) {
    return undefined;
  }

  return dateTimeInputPartsToUtcMilli(
    value,
    boundary === "start" ? "00:00" : "23:59",
    timeZone,
  );
};

// Must invert dateInputToUtcMilli exactly: sales dates are stored as 00:00 /
// 23:59 in the CHAMBER's zone, so reading them back through UTC (toISOString)
// shifted the end boundary +1 day per load/save cycle in every UTC-negative
// chamber zone.
const utcMilliToDateInput = (
  value: number | null | undefined,
  timeZone: string,
): string => (value ? utcMilliToDateTimeInputParts(value, timeZone).date : "");

const getDressCodeEnum = (
  dressCode?: EventFormState["dressCode"],
): CmpEventDressCodeEnum | undefined => {
  switch (dressCode) {
    case "Formal":
      return CmpEventDressCodeEnum.Formal;
    case "Business Attire":
      return CmpEventDressCodeEnum.BusinessAttire;
    case "Casual":
      return CmpEventDressCodeEnum.Casual;
    default:
      return undefined;
  }
};

const getDressCodeLabel = (
  dressCode?: CmpEventDressCodeEnum | null,
): EventFormState["dressCode"] => {
  switch (dressCode) {
    case CmpEventDressCodeEnum.Formal:
      return "Formal";
    case CmpEventDressCodeEnum.BusinessAttire:
      return "Business Attire";
    case CmpEventDressCodeEnum.Casual:
      return "Casual";
    default:
      return "";
  }
};

/**
 * Staff often type a bare domain ("www.chamber.com"). Add https:// when no
 * scheme is present so the Event Button Link is accepted and saved as a
 * working link. Returns "" for blank input.
 */
/**
 * Design: preselect meal options have no separate switch. Any saved meal
 * option means attendees choose a meal instead of the dietary checkboxes.
 */
export const hasPreselectMeals = (question: {
  mealOptions?: string[] | null;
}): boolean => (question.mealOptions || []).length > 0;

/**
 * The Schedule Event pop-up's "Internal Review Note" is kept as an extra
 * staff note (the first note stays the editable internal note).
 */
export const appendScheduleReviewNote = (
  event: EventFormState,
  note: string
): EventFormState => {
  const body = note.trim();
  if (!body) return event;
  const [firstNote, ...otherNotes] = event.savedNotes || [];
  return {
    ...event,
    savedNotes: [
      firstNote || { id: "internal-note", body: event.internalNotes || "", visibility: "STAFF" },
      ...otherNotes,
      { id: `schedule-note-${Date.now()}`, body: `Schedule review note: ${body}`, visibility: "STAFF" },
    ],
  };
};

export const normalizeEventButtonUrl = (value: string | null | undefined): string => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  return /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const toEventTypeOptions = (
  eventTypes: EventTypeReturn[],
): EventTypeOption[] =>
  eventTypes.map((eventType) => ({
    id: eventType.id,
    label: eventType.label,
    color: eventType.color,
    isArchived: eventType.isArchived,
  }));

export const buildBlankEventFormState = (
  eventTypes: EventTypeOption[],
): EventFormState => createBlankEventFormState();

const getStatusLabel = (status: CmpEventStatusEnum): EventStatus => {
  switch (status) {
    case CmpEventStatusEnum.Scheduled:
      return "Scheduled";
    case CmpEventStatusEnum.Published:
      return "Published";
    case CmpEventStatusEnum.Live:
      return "Live";
    case CmpEventStatusEnum.Completed:
      return "Completed";
    case CmpEventStatusEnum.Canceled:
      return "Cancelled";
    case CmpEventStatusEnum.Draft:
    default:
      return "Draft";
  }
};

const getVisibilityLabel = (
  visibility: CmpEventVisibilityEnum,
): EventVisibility =>
  visibility === CmpEventVisibilityEnum.Private ? "Private" : "Public";

const getFieldTypeLabel = (
  fieldType: CmpEventCustomFieldTypeEnum,
): EventCustomField["type"] => {
  switch (fieldType) {
    case CmpEventCustomFieldTypeEnum.Select:
      return "Select";
    case CmpEventCustomFieldTypeEnum.MultiSelect:
      return "Multi-select";
    case CmpEventCustomFieldTypeEnum.Checkbox:
      return "Checkbox";
    case CmpEventCustomFieldTypeEnum.FileUpload:
      return "File upload";
    case CmpEventCustomFieldTypeEnum.Text:
    case CmpEventCustomFieldTypeEnum.Textarea:
    case CmpEventCustomFieldTypeEnum.Date:
    default:
      return "Text";
  }
};

const formatCustomFieldChoices = (choices: string[]): string =>
  choices.join(", ");

const parseCustomFieldChoices = (choices: string): string[] =>
  choices
    .split(",")
    .map((choice) => choice.trim())
    .filter(Boolean);

const getCustomFieldChoicesForSave = ({
  backendChoices,
  choices,
}: Pick<EventCustomField, "backendChoices" | "choices">): string[] =>
  backendChoices && formatCustomFieldChoices(backendChoices) === choices
    ? [...backendChoices]
    : parseCustomFieldChoices(choices);

const defaultRegistrationQuestions = createDefaultRegistrationQuestions();
const registrationQuestionIds = new Set(
  defaultRegistrationQuestions.map((question) => question.id),
);
const registrationQuestionPresetKeys = new Set(
  defaultRegistrationQuestions.map((question) => question.presetKey),
);
const REGISTRATION_QUESTION_SETTINGS_VERSION = 1;
const normalizeRegistrationQuestionLabel = (label: string): string =>
  label.trim().toLocaleLowerCase("en-CA");
// Chambers are Canadian: helper text saved with "panelists" loads as
// "panellists" and is saved that way on the next Update Event (North Bay,
// Oct 2026).
export const toCanadianPanellistSpelling = (value: string): string =>
  value.replace(/\b([Pp])anelist(s?)\b/g, "$1anellist$2");
// Earlier default titles of the fixed preset questions. Saved events keep
// them; they still identify the preset and show the current design title
// (IF11 EV8 / Q14, no rewrite of saved events).
const legacyRegistrationQuestionTitles: ReadonlyArray<[string, string]> = [
  ["We are able to offer the following dietary option.", "question-dietary"],
];
const legacyRegistrationQuestionTitleIds = new Map(
  legacyRegistrationQuestionTitles.map(([title, id]): [string, string] => [
    normalizeRegistrationQuestionLabel(title),
    id,
  ]),
);
const getRegistrationQuestionDisplayTitle = (
  question: Pick<EventRegistrationQuestion, "adminTitle" | "id">,
  savedTitle: string | null | undefined,
): string =>
  !savedTitle ||
  legacyRegistrationQuestionTitleIds.get(normalizeRegistrationQuestionLabel(savedTitle)) === question.id
    ? question.adminTitle
    : savedTitle;
const registrationQuestionIdByLabel = new Map([
  ...defaultRegistrationQuestions.map((question): [string, string] => [
    normalizeRegistrationQuestionLabel(question.adminTitle),
    question.id,
  ]),
  ...Array.from(legacyRegistrationQuestionTitleIds.entries()),
  // Events saved by the legacy custom-field UI used a generated id and this
  // public-facing label for the speaker preset.
  [
    normalizeRegistrationQuestionLabel(
      "Submit a question to the speaker or panelists.",
    ),
    "question-speaker",
  ] as [string, string],
  [
    normalizeRegistrationQuestionLabel(
      "Submit a question to the speaker or panellists.",
    ),
    "question-speaker",
  ] as [string, string],
]);

type RegistrationQuestionCustomField = Pick<
  CmpEventCustomFieldReturn,
  | "appliesToSponsorshipPackageIds"
  | "appliesToTicketTypeIds"
  | "choices"
  | "fieldType"
  | "helperText"
  | "id"
  | "label"
  | "required"
  | "scope"
  | "sortOrder"
>;

type CustomFieldsToRegistrationQuestionsInput = {
  customFields: RegistrationQuestionCustomField[];
  registrationQuestionSettingsJson?: string | null;
};

type RegistrationQuestionSetting = {
  preselectMealsEnabled?: boolean;
  mealOptions?: string[];
  id: string;
  adminTitle: string;
  description: string;
  enabled: boolean;
  choices: Array<{ id: string; enabled: boolean }>;
  label: string;
  helperText: string;
  customFieldId: string;
  required: boolean;
  backendFieldType: CmpEventCustomFieldTypeEnum;
  scope: CmpEventCustomFieldScopeEnum;
  appliesToTicketTypeIds: string[];
  appliesToSponsorshipPackageIds: string[];
  sortOrder: number;
};

type RegistrationQuestionSettings = {
  version: typeof REGISTRATION_QUESTION_SETTINGS_VERSION;
  questions: RegistrationQuestionSetting[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((entry) => typeof entry === "string");

const customFieldTypeValues = new Set<string>(
  Object.values(CmpEventCustomFieldTypeEnum),
);
const customFieldScopeValues = new Set<string>(
  Object.values(CmpEventCustomFieldScopeEnum),
);

const isCustomFieldType = (
  value: unknown,
): value is CmpEventCustomFieldTypeEnum =>
  typeof value === "string" && customFieldTypeValues.has(value);

const isCustomFieldScope = (
  value: unknown,
): value is CmpEventCustomFieldScopeEnum =>
  typeof value === "string" && customFieldScopeValues.has(value);

const getDefaultRegistrationQuestionFieldType = (
  question: EventRegistrationQuestion,
): CmpEventCustomFieldTypeEnum =>
  question.kind === "multiSelect"
    ? CmpEventCustomFieldTypeEnum.MultiSelect
    : CmpEventCustomFieldTypeEnum.Textarea;

const withRegistrationQuestionMetadata = (
  question: EventRegistrationQuestion,
  index: number,
): EventRegistrationQuestion => ({
  ...question,
  customFieldId: question.customFieldId || question.id,
  // Preset questions have no Required control in Create Event, so they always
  // save as optional. Older events kept a hidden required: true that forced a
  // dietary pick on every attendee (Northern Corridor BAG, Oct 2026).
  required: false,
  backendFieldType:
    question.backendFieldType ||
    getDefaultRegistrationQuestionFieldType(question),
  scope: question.scope || CmpEventCustomFieldScopeEnum.Attendee,
  appliesToTicketTypeIds: question.appliesToTicketTypeIds || [],
  appliesToSponsorshipPackageIds: question.appliesToSponsorshipPackageIds || [],
  sortOrder: question.sortOrder ?? index,
});

const expectedFieldTypesByQuestionId = new Map<string, Set<string>>(
  defaultRegistrationQuestions.map((question) => [
    question.id,
    new Set(
      question.kind === "multiSelect"
        ? [CmpEventCustomFieldTypeEnum.MultiSelect, CmpEventCustomFieldTypeEnum.Select]
        : [
            CmpEventCustomFieldTypeEnum.Text,
            CmpEventCustomFieldTypeEnum.Textarea,
          ],
    ),
  ]),
);

export const getRegistrationQuestionIdForCustomField = (
  customField: RegistrationQuestionCustomField,
): string | undefined => {
  if (registrationQuestionIds.has(customField.id)) {
    return customField.id;
  }

  const questionId = registrationQuestionIdByLabel.get(
    normalizeRegistrationQuestionLabel(customField.label),
  );
  if (
    !questionId ||
    customField.scope !== CmpEventCustomFieldScopeEnum.Attendee ||
    !expectedFieldTypesByQuestionId.get(questionId)?.has(customField.fieldType)
  ) {
    return undefined;
  }

  return questionId;
};

export type EventAttendeeChoiceFields = Readonly<{
  accommodations: CmpEventCustomFieldReturn | null;
  dietary: CmpEventCustomFieldReturn | null;
  /** The dietary question holds preselected meal options (one choice). */
  dietaryIsMealChoice: boolean;
  /** The enabled "Submit Speaker Question" text field, if any. */
  speaker: CmpEventCustomFieldReturn | null;
}>;

export const emptyEventAttendeeChoiceFields: EventAttendeeChoiceFields = {
  accommodations: null,
  dietary: null,
  dietaryIsMealChoice: false,
  speaker: null,
};

type GetEventAttendeeChoiceFieldsInput = Readonly<{
  customFields: ReadonlyArray<RegistrationQuestionCustomField>;
  ticketTypeId?: string | null;
}>;

export const getEventAttendeeChoiceFields = ({
  customFields,
  ticketTypeId,
}: GetEventAttendeeChoiceFieldsInput): EventAttendeeChoiceFields => {
  let accommodations: CmpEventCustomFieldReturn | null = null;
  let dietary: CmpEventCustomFieldReturn | null = null;
  let speaker: CmpEventCustomFieldReturn | null = null;

  customFields.forEach((field) => {
    if (
      field.scope !== CmpEventCustomFieldScopeEnum.Attendee ||
      (ticketTypeId != null &&
        field.appliesToTicketTypeIds.length > 0 &&
        !field.appliesToTicketTypeIds.includes(ticketTypeId))
    ) {
      return;
    }

    // Use the wizard's preset identity rule so legacy field IDs still work.
    const questionId = getRegistrationQuestionIdForCustomField(field);
    // The speaker question is free text; choice questions need choices.
    if (questionId === "question-speaker" && !speaker) {
      speaker = field;
    }
    if (field.choices.length === 0) {
      return;
    }
    if (questionId === "question-accommodations" && !accommodations) {
      accommodations = field;
    }
    if (questionId === "question-dietary" && !dietary) {
      dietary = field;
    }
  });

  return {
    accommodations,
    dietary,
    // Preselected meals save the dietary question as a single Select
    // (see eventFormStateToCustomFields); plain dietary options stay
    // MultiSelect.
    dietaryIsMealChoice:
      (dietary as CmpEventCustomFieldReturn | null)?.fieldType ===
      CmpEventCustomFieldTypeEnum.Select,
    speaker,
  };
};

export const isRegistrationQuestionCustomField = (
  customField: RegistrationQuestionCustomField,
): boolean => Boolean(getRegistrationQuestionIdForCustomField(customField));

const isRegistrationQuestionFormCustomField = ({
  id,
  presetKey,
}: Pick<EventCustomField, "id" | "presetKey">): boolean =>
  registrationQuestionIds.has(id) ||
  Boolean(presetKey && registrationQuestionPresetKeys.has(presetKey));

export const registrationQuestionsToSettingsJson = ({
  registrationQuestions,
}: RegistrationQuestionsToCustomFieldInputsInput): string => {
  const questions = (
    registrationQuestions || createDefaultRegistrationQuestions()
  ).map(withRegistrationQuestionMetadata);
  const settings: RegistrationQuestionSettings = {
    version: REGISTRATION_QUESTION_SETTINGS_VERSION,
    questions: questions.map((question) => ({
      id: question.id,
      preselectMealsEnabled: hasPreselectMeals(question),
      mealOptions: question.mealOptions || [],
      adminTitle: question.adminTitle,
      description: question.description,
      enabled: question.enabled,
      choices: question.choices.map((choice) => ({
        id: choice.id,
        enabled: choice.enabled,
      })),
      label: question.label,
      helperText: question.helperText,
      customFieldId: question.customFieldId || question.id,
      required: question.required ?? false,
      backendFieldType:
        question.backendFieldType ||
        getDefaultRegistrationQuestionFieldType(question),
      scope: question.scope || CmpEventCustomFieldScopeEnum.Attendee,
      appliesToTicketTypeIds: question.appliesToTicketTypeIds || [],
      appliesToSponsorshipPackageIds:
        question.appliesToSponsorshipPackageIds || [],
      sortOrder: question.sortOrder ?? 0,
    })),
  };

  return JSON.stringify(settings);
};

export const registrationQuestionSettingsJsonToQuestions = (
  settingsJson?: string | null,
): EventRegistrationQuestion[] | null => {
  if (!settingsJson) {
    return null;
  }

  let parsedSettings: unknown;
  try {
    parsedSettings = JSON.parse(settingsJson) as unknown;
  } catch {
    return null;
  }

  if (
    !isRecord(parsedSettings) ||
    parsedSettings.version !== REGISTRATION_QUESTION_SETTINGS_VERSION ||
    !Array.isArray(parsedSettings.questions)
  ) {
    return null;
  }

  const settingById = new Map<string, Record<string, unknown>>();
  parsedSettings.questions.forEach((setting) => {
    if (isRecord(setting) && typeof setting.id === "string") {
      settingById.set(setting.id, setting);
    }
  });

  const questions = createDefaultRegistrationQuestions();
  if (questions.some((question) => !settingById.has(question.id))) {
    return null;
  }

  const restoredQuestions: EventRegistrationQuestion[] = [];
  for (let index = 0; index < questions.length; index += 1) {
    const question = questions[index];
    const setting = settingById.get(question.id);
    if (!setting || typeof setting.enabled !== "boolean") {
      return null;
    }

    const choiceSettings = Array.isArray(setting.choices)
      ? setting.choices
      : [];
    const enabledByChoiceId = new Map<string, boolean>();
    for (const choiceSetting of choiceSettings) {
      if (
        !isRecord(choiceSetting) ||
        typeof choiceSetting.id !== "string" ||
        typeof choiceSetting.enabled !== "boolean"
      ) {
        return null;
      }
      enabledByChoiceId.set(choiceSetting.id, choiceSetting.enabled);
    }
    if (question.choices.some((choice) => !enabledByChoiceId.has(choice.id))) {
      return null;
    }

    const defaultQuestion = withRegistrationQuestionMetadata(question, index);
    restoredQuestions.push({
      ...defaultQuestion,
      preselectMealsEnabled: setting.preselectMealsEnabled === true,
      mealOptions: isStringArray(setting.mealOptions) ? setting.mealOptions : [],
      adminTitle: getRegistrationQuestionDisplayTitle(
        question,
        typeof setting.adminTitle === "string" ? setting.adminTitle : null,
      ),
      description:
        typeof setting.description === "string"
          ? setting.description
          : question.description,
      enabled: setting.enabled,
      choices: question.choices.map((choice) => ({
        ...choice,
        enabled: enabledByChoiceId.get(choice.id) ?? choice.enabled,
      })),
      label: typeof setting.label === "string" ? setting.label : question.label,
      helperText: toCanadianPanellistSpelling(
        typeof setting.helperText === "string"
          ? setting.helperText
          : question.helperText,
      ),
      customFieldId:
        typeof setting.customFieldId === "string" && setting.customFieldId
          ? setting.customFieldId
          : question.id,
      required:
        typeof setting.required === "boolean" ? setting.required : false,
      backendFieldType: isCustomFieldType(setting.backendFieldType)
        ? setting.backendFieldType
        : defaultQuestion.backendFieldType,
      scope: isCustomFieldScope(setting.scope)
        ? setting.scope
        : CmpEventCustomFieldScopeEnum.Attendee,
      appliesToTicketTypeIds: isStringArray(setting.appliesToTicketTypeIds)
        ? setting.appliesToTicketTypeIds
        : [],
      appliesToSponsorshipPackageIds: isStringArray(
        setting.appliesToSponsorshipPackageIds,
      )
        ? setting.appliesToSponsorshipPackageIds
        : [],
      sortOrder:
        typeof setting.sortOrder === "number" &&
        Number.isFinite(setting.sortOrder)
          ? setting.sortOrder
          : index,
    });
  }

  return restoredQuestions;
};

// Enabled questions use the event custom-field contract so the public
// registration form can render them. The settings JSON is the admin source of
// truth for disabled parents/choices. Legacy records without it are inferred
// only from recognized custom fields. An absent field means that the public
// form did not ask the question, so the editor must not invent an enabled
// question when it reloads the event.
export const customFieldsToRegistrationQuestions = ({
  customFields,
  registrationQuestionSettingsJson,
}: CustomFieldsToRegistrationQuestionsInput): EventRegistrationQuestion[] => {
  const questions = createDefaultRegistrationQuestions().map(
    withRegistrationQuestionMetadata,
  );
  const customFieldById = new Map<string, RegistrationQuestionCustomField>();
  customFields.forEach((customField) => {
    const stableQuestionId =
      getRegistrationQuestionIdForCustomField(customField);
    if (!stableQuestionId) {
      return;
    }

    const existingField = customFieldById.get(stableQuestionId);
    if (!existingField || customField.id === stableQuestionId) {
      customFieldById.set(stableQuestionId, customField);
    }
  });
  const settingsQuestions = registrationQuestionSettingsJsonToQuestions(
    registrationQuestionSettingsJson,
  );
  if (settingsQuestions) {
    const customFieldBySourceId = new Map(
      customFields.map((customField) => [customField.id, customField]),
    );
    return settingsQuestions.map((question) => {
      const sourceField =
        customFieldBySourceId.get(question.customFieldId || question.id) ||
        customFieldById.get(question.id);
      if (!sourceField) {
        return question;
      }

      return {
        ...question,
        customFieldId: sourceField.id,
        required: sourceField.required,
        backendFieldType: sourceField.fieldType,
        scope: sourceField.scope,
        appliesToTicketTypeIds: sourceField.appliesToTicketTypeIds,
        appliesToSponsorshipPackageIds:
          sourceField.appliesToSponsorshipPackageIds,
        sortOrder: sourceField.sortOrder ?? question.sortOrder,
      };
    });
  }
  const hasPersistedRegistrationQuestions = customFieldById.size > 0;

  if (!hasPersistedRegistrationQuestions) {
    return questions.map((question) => ({ ...question, enabled: false }));
  }

  return questions.map((question) => {
    const customField = customFieldById.get(question.id);
    if (!customField) {
      return { ...question, enabled: false };
    }

    const metadata = {
      customFieldId: customField.id,
      required: customField.required,
      backendFieldType: customField.fieldType,
      scope: customField.scope,
      appliesToTicketTypeIds: customField.appliesToTicketTypeIds,
      appliesToSponsorshipPackageIds:
        customField.appliesToSponsorshipPackageIds,
      sortOrder: customField.sortOrder ?? question.sortOrder,
    };

    if (question.kind === "speakerQuestion") {
      return {
        ...question,
        ...metadata,
        enabled: true,
        label:
          customField.label === question.adminTitle ? "" : customField.label,
        helperText: toCanadianPanellistSpelling(
          customField.helperText || question.helperText,
        ),
      };
    }

    const enabledChoiceLabels = new Set(
      customField.choices.map(normalizeRegistrationQuestionLabel),
    );

    return {
      ...question,
      ...metadata,
      adminTitle: getRegistrationQuestionDisplayTitle(question, customField.label),
      description: customField.helperText || question.description,
      enabled: true,
      choices: question.choices.map((choice) => ({
        ...choice,
        enabled: enabledChoiceLabels.has(
          normalizeRegistrationQuestionLabel(choice.label),
        ),
      })),
    };
  });
};

type RegistrationQuestionsToCustomFieldInputsInput = {
  registrationQuestions?: EventRegistrationQuestion[];
};

export const registrationQuestionsToCustomFieldInputs = ({
  registrationQuestions,
}: RegistrationQuestionsToCustomFieldInputsInput): CmpEventCustomFieldInput[] =>
  (registrationQuestions || createDefaultRegistrationQuestions())
    .map(withRegistrationQuestionMetadata)
    .flatMap((question, index) => {
      if (!question.enabled) {
        return [];
      }

      const isMultiSelect = question.kind === "multiSelect";
      return [
        {
          id: question.customFieldId || question.id,
          label:
            question.kind === "speakerQuestion"
              ? question.label.trim() || question.adminTitle
              : question.adminTitle,
          fieldType: question.id === "question-dietary" && hasPreselectMeals(question)
            ? CmpEventCustomFieldTypeEnum.Select
            : question.backendFieldType || getDefaultRegistrationQuestionFieldType(question),
          scope: question.scope || CmpEventCustomFieldScopeEnum.Attendee,
          required: question.required ?? false,
          helperText: isMultiSelect
            ? question.description || undefined
            : question.helperText || question.description || undefined,
          choices: question.id === "question-dietary" && hasPreselectMeals(question)
            ? question.mealOptions || []
            : isMultiSelect ? question.choices
                .filter((choice) => choice.enabled)
                .map((choice) => choice.label)
            : [],
          appliesToTicketTypeIds: question.appliesToTicketTypeIds || [],
          appliesToSponsorshipPackageIds:
            question.appliesToSponsorshipPackageIds || [],
          sortOrder: question.sortOrder ?? index,
        },
      ];
    });

export const eventReturnToFormState = (
  event: CmpEventFieldsFragment,
  timeZone?: string | null,
): EventFormState => {
  const eventTimeZone = timeZone || event.timeZone;
  const start = utcMilliToDateTimeInputParts(
    event.startAtUtcMilli,
    eventTimeZone,
  );
  const end = utcMilliToDateTimeInputParts(event.endAtUtcMilli, eventTimeZone);
  // The API requires a mode, so a Draft saved with no format chosen comes back
  // as In-person with no details. Show it as not chosen, not as a staff choice.
  const { venueName, streetAddress, city, province, postalCode, country, latitude, longitude } = event.location;
  const draftFormatNotChosen =
    event.status === CmpEventStatusEnum.Draft &&
    event.eventKind !== CmpEventKindEnum.Community &&
    event.location.mode === CmpEventLocationModeEnum.InPerson &&
    !`${venueName || ""}${streetAddress || ""}${city || ""}${province || ""}${postalCode || ""}${country || ""}`.trim() &&
    latitude == null &&
    longitude == null;
  const inPersonEnabled =
    !draftFormatNotChosen &&
    (event.location.mode === CmpEventLocationModeEnum.InPerson ||
      event.location.mode === CmpEventLocationModeEnum.Hybrid);
  const virtualEnabled =
    event.location.mode === CmpEventLocationModeEnum.Virtual ||
    event.location.mode === CmpEventLocationModeEnum.Hybrid;
  // Decision Q15: Claude Design (8) removed the event-level "do not show
  // sponsorships on your website" box. An event saved with it ticked loads
  // with every level's "do not show this sponsorship on your event page" box
  // ticked and the event flag shown, so the next save keeps the same public
  // result per level (Create Event Screen.dc.html:1440-1445).
  const sponsorshipsHiddenForEvent = event.sponsorshipPublicDisplayEnabled === false;

  return {
    id: event.id,
    slug: event.slug,
    updatedAtUtcMilli: event.updatedAtUtcMilli,
    eventKind: event.eventKind ?? CmpEventKindEnum.Standard,
    externalEventUrl: event.externalEventUrl || "",
    startDateOnly: !!event.startDateOnly,
    endDateOnly: !!event.endDateOnly,
    publishAtUtcMilli: event.publishAtUtcMilli,
    preCheckInEnabled: event.preCheckInEnabled ?? true,
    preCheckInHours: event.preCheckInHours ?? 72,
    sponsorshipPublicDisplayEnabled: true,
    sponsorshipTaxMode: event.sponsorshipTaxMode ?? CmpEventSponsorshipTaxModeEnum.Full,
    title: event.title,
    eventTypeId: event.eventTypeId,
    eventType: event.eventTypeLabelSnapshot,
    eventTypeColor: event.eventTypeColorSnapshot,
    description: event.description,
    featuredBanner: event.featuredImageUrl || "",
    savedStartAtUtcMilli: event.startAtUtcMilli,
    savedEndAtUtcMilli: event.endAtUtcMilli,
    startDate: start.date,
    startTime: event.startDateOnly ? "" : start.time,
    endDate: end.date,
    endTime: event.endDateOnly ? "" : end.time,
    inPersonEnabled,
    virtualEnabled,
    venueName: event.location.venueName || "",
    address: event.location.streetAddress || "",
    virtualAccessInstructions: event.location.virtualDetails || "",
    capacity: event.maxAttendeeCapacity
      ? String(event.maxAttendeeCapacity)
      : "",
    publicRegistrationEnabled: event.publicRegistrationEnabled,
    tickets: event.tickets.map((ticket) => ({
      id: ticket.id,
      persisted: true,
      name: ticket.name,
      description: ticket.description || "",
      // POL-005: editable prices load as the canonical CurrencyInput shape
      // ("85.00", no "$"/commas) so an untouched Update reload stays
      // JSON-equal to the wizard form state (hasUnsavedChanges stability).
      price: centsToCurrencyValue(ticket.priceInCents),
      attendeesPerTicket: String(ticket.attendeesPerTicket || 1),
      // Saved tickets keep their saved audience; Step 3 applies the name rule
      // only when a ticket is renamed (decision Q13).
      memberOnly: ticket.audience === CmpEventTicketAudienceEnum.ActiveMembers,
      audience: ticket.audience,
      attendanceMode: ticket.attendanceMode,
      savedSalesStartUtcMilli: ticket.salesStartUtcMilli,
      savedSalesEndUtcMilli: ticket.salesEndUtcMilli,
      code: ticket.code || "",
      salesStartDate: utcMilliToDateInput(
        ticket.salesStartUtcMilli,
        eventTimeZone,
      ),
      salesEndDate: utcMilliToDateInput(ticket.salesEndUtcMilli, eventTimeZone),
      availabilityLimit:
        ticket.availabilityLimit != null
          ? String(ticket.availabilityLimit)
          : "",
      visibleOnRegistrationForm:
        ticket.visibleOnRegistrationForm ??
        (ticket.audience !== CmpEventTicketAudienceEnum.SponsorIncluded &&
          !isInternalFreeTicketSeed(ticket)),
      noTax: ticket.noTax === true,
    })),
    customFields: event.customFields
      .filter((customField) => !isRegistrationQuestionCustomField(customField))
      .map((customField) => ({
        id: customField.id,
        label: customField.label,
        type: getFieldTypeLabel(customField.fieldType),
        required: customField.required,
        choices: formatCustomFieldChoices(customField.choices),
        backendChoices: customField.choices,
        backendFieldType: customField.fieldType,
        scope: customField.scope,
        helperText: customField.helperText || undefined,
        appliesToTicketTypeIds: customField.appliesToTicketTypeIds,
        appliesToSponsorshipPackageIds:
          customField.appliesToSponsorshipPackageIds,
        sortOrder: customField.sortOrder ?? undefined,
      })),
    sponsorshipEnabled: event.sponsorshipEnabled,
    sponsorshipPackages: event.sponsorshipPackages.map(
      (sponsorshipPackage) => ({
        id: sponsorshipPackage.id,
        name: sponsorshipPackage.name,
        description: sponsorshipPackage.description || "",
        // POL-005: canonical decimal-dollars shape (see ticket.price above).
        price: centsToCurrencyValue(sponsorshipPackage.priceInCents),
        maxAvailable: sponsorshipPackage.maxAvailable
          ? String(sponsorshipPackage.maxAvailable)
          : "",
        includedTickets: String(
          sponsorshipPackage.includedAttendeeQuantity || 0,
        ),
        includedTicketTypeId:
          sponsorshipPackage.includedAttendeeTicketTypeId || "",
        includedTicketValue: centsToCurrencyValue(sponsorshipPackage.includedTicketValueInCents || 0),
        publicDisplayEnabled: sponsorshipsHiddenForEvent ? false : sponsorshipPackage.publicDisplayEnabled,
        // Sponsorships & Advertising listing (Monday 13211717938, Moeen Q20):
        // a level saved before the S&A box existed keeps its old meaning, so
        // a level hidden from the event page stays hidden from S&A too.
        websiteListingEnabled:
          sponsorshipPackage.websiteListingEnabled ?? sponsorshipPackage.publicDisplayEnabled !== false,
        logoRequired: sponsorshipPackage.logoRequired,
        linkRequired: sponsorshipPackage.linkRequired,
        code: sponsorshipPackage.code || "",
        heroBannerEnabled: !!sponsorshipPackage.heroBannerEnabled,
        additionalBenefits: sponsorshipPackage.additionalBenefits || "",
        addOns: (sponsorshipPackage.addOns || []).map((addOn) => ({
          id: addOn.id,
          description: addOn.description,
          code: addOn.code || "",
          quantity: String(addOn.quantity),
          unitValue: centsToCurrencyValue(addOn.unitValueInCents || 0),
          taxable: !!addOn.taxable,
        })),
      }),
    ),
    costs: event.internalCosts.map((cost) => ({
      id: cost.id,
      label: cost.label,
      amount: formatCents(cost.amountInCents),
      note: cost.note || undefined,
    })),
    responsibilities: event.responsibilities.map((responsibility) => ({
      id: responsibility.id,
      owner: responsibility.assignedTo || "",
      task: responsibility.label,
      note: responsibility.note || undefined,
    })),
    internalNotes: event.notes[0]?.body || "",
    savedNotes: event.notes.map(({ id, body, visibility }) => ({ id, body, visibility })),
    latitude: event.location.latitude,
    longitude: event.location.longitude,
    status: getStatusLabel(event.status),
    visibility: getVisibilityLabel(event.visibility),
    attendeeCount: event.attendeeCount,
    sponsorRequestCount: 0,
    ticketRevenue: event.ticketRevenueInCents / 100,
    sponsorshipRevenue: event.sponsorshipRevenueInCents / 100,
    eventCode: event.eventCode || "",
    heroShortDescription: event.heroShortDescription || "",
    hideHeroTitle: event.hideHeroTitle === true,
    registrationDetailsHtml: event.registrationDetailsHtml || "",
    specialVenueConsiderations: event.specialVenueConsiderations || "",
    dressCode: getDressCodeLabel(event.dressCode),
    speakers: resolveEventSpeakerKeynotes((event.speakers || []).map((speaker) => ({
      id: speaker.id,
      name: speaker.name,
      company: speaker.company || "",
      title: speaker.title || "",
      imageUrl: speaker.imageUrl || "",
      bio: speaker.bio || "",
      isKeynote: speaker.isKeynote ?? null,
    }))),
    partners: (event.partners || []).map((partner) => ({
      id: partner.id,
      logoUrl: partner.logoUrl,
      websiteUrl: partner.websiteUrl || "",
    })),
    agendaItems: (event.agendaItems || []).map((agendaItem) => ({
      id: agendaItem.id,
      timeLabel: agendaItem.timeLabel,
      details: agendaItem.details,
    })),
    eventIsFree: !!event.eventIsFree,
    displayRemainingTicketQuantity:
      event.displayRemainingTicketQuantity ?? true,
    displayRemainingTicketTypeQuantity:
      !!event.displayRemainingTicketTypeQuantity,
    allowWaitlist: !!event.allowWaitlist,
    city: event.location.city || "",
    province: event.location.province || "",
    postalCode: event.location.postalCode || "",
    country: event.location.country || "",
    registrationQuestions: customFieldsToRegistrationQuestions({
      customFields: event.customFields,
      registrationQuestionSettingsJson: event.registrationQuestionSettingsJson,
    }),
  };
};

const getStatusEnum = (status: EventStatus): CmpEventStatusEnum => {
  switch (status) {
    case "Scheduled":
      return CmpEventStatusEnum.Scheduled;
    case "Published":
      return CmpEventStatusEnum.Published;
    case "Live":
      return CmpEventStatusEnum.Live;
    case "Completed":
      return CmpEventStatusEnum.Completed;
    case "Cancelled":
      return CmpEventStatusEnum.Canceled;
    case "Draft":
    default:
      return CmpEventStatusEnum.Draft;
  }
};

// No visibility chosen yet (Decision Q12) sends nothing: the API keeps the
// saved value or defaults a new event to Public, and a draft reopens with
// none selected (eventDraftDocument). Publish, Schedule and Update require a
// choice for standard events (EventWizard).
const getVisibilityEnum = (
  visibility: EventVisibility | "",
): CmpEventVisibilityEnum | undefined =>
  visibility === "Private"
    ? CmpEventVisibilityEnum.Private
    : visibility === "Public" ? CmpEventVisibilityEnum.Public : undefined;

const getFieldTypeEnum = (
  fieldType: EventCustomField["type"],
): CmpEventCustomFieldTypeEnum => {
  switch (fieldType) {
    case "Select":
      return CmpEventCustomFieldTypeEnum.Select;
    case "Multi-select":
      return CmpEventCustomFieldTypeEnum.MultiSelect;
    case "Checkbox":
      return CmpEventCustomFieldTypeEnum.Checkbox;
    case "File upload":
      return CmpEventCustomFieldTypeEnum.FileUpload;
    case "Text":
    default:
      return CmpEventCustomFieldTypeEnum.Text;
  }
};

const isTicketVisibleOnRegistrationForm = (
  ticket: EventFormState["tickets"][number],
) =>
  ticket.visibleOnRegistrationForm ??
  (ticket.audience !== CmpEventTicketAudienceEnum.SponsorIncluded &&
    !isInternalFreeTicketSeed(ticket));

const isVisibleZeroPriceTicket = (ticket: EventFormState["tickets"][number]) =>
  !ticket.memberOnly &&
  parseCents(ticket.price) === 0 &&
  isTicketVisibleOnRegistrationForm(ticket);

/**
 * Decision Q13 (Claude Design (8) removed the "Members only" box): a ticket is
 * members-only only when its name says "member" and it is not a non-member
 * ticket, e.g. "Member Ticket" or "Member Only Ticket" (the design's own
 * helper, Create Event Screen.dc.html:1326). "Table of 8", "Foursome" and
 * "Complimentary Ticket" stay open to everyone.
 */
export const isMemberOnlyTicketName = (name: string): boolean =>
  /member/i.test(name) && !/non[\s-]?member/i.test(name);

const getTicketAudienceForSave = (
  ticket: EventFormState["tickets"][number],
): CmpEventTicketAudienceEnum => {
  // Sponsor-included tickets keep their audience whatever their name.
  if (ticket.audience === CmpEventTicketAudienceEnum.SponsorIncluded) {
    return ticket.audience;
  }

  if (ticket.memberOnly) {
    return CmpEventTicketAudienceEnum.ActiveMembers;
  }

  if (ticket.audience === CmpEventTicketAudienceEnum.NonMembers) {
    return ticket.audience;
  }

  return CmpEventTicketAudienceEnum.Everyone;
};

const buildFreeEventTicketInput = (event: EventFormState) => {
  const legitimatePublicFreeTicket = event.tickets.find(
    (ticket) =>
      isVisibleZeroPriceTicket(ticket) &&
      ticket.audience !== CmpEventTicketAudienceEnum.SponsorIncluded &&
      !isInternalFreeTicketSeed(ticket),
  );
  const visibleLegacyInternalTicket = event.tickets.find(
    (ticket) =>
      isVisibleZeroPriceTicket(ticket) && isInternalFreeTicketSeed(ticket),
  );
  const existingPublicFreeTicket =
    legitimatePublicFreeTicket || visibleLegacyInternalTicket;
  const isRecoveringLegacyInternalTicket =
    !!existingPublicFreeTicket &&
    isInternalFreeTicketSeed(existingPublicFreeTicket);

  return {
    id: existingPublicFreeTicket?.id || FREE_EVENT_TICKET_ID,
    code: isRecoveringLegacyInternalTicket
      ? FREE_EVENT_TICKET_CODE
      : existingPublicFreeTicket?.code || FREE_EVENT_TICKET_CODE,
    name: FREE_EVENT_TICKET_NAME,
    description: isRecoveringLegacyInternalTicket
      ? FREE_EVENT_TICKET_DESCRIPTION
      : existingPublicFreeTicket?.description || FREE_EVENT_TICKET_DESCRIPTION,
    priceInCents: 0,
    attendeesPerTicket: 1,
    audience: CmpEventTicketAudienceEnum.Everyone,
    attendanceMode: CmpEventAttendanceModeEnum.Either,
    salesStartUtcMilli: undefined,
    salesEndUtcMilli: undefined,
    availabilityLimit: isRecoveringLegacyInternalTicket
      ? null
      : parseOptionalNumber(existingPublicFreeTicket?.availabilityLimit || ""),
    visibleOnRegistrationForm: true,
    sortOrder: 0,
  };
};

// A date-only control must not replace an unchanged, precise sales boundary.
const savedSalesBoundaryToUtcMilli = ({ savedUtcMilli, ...input }: {
  savedUtcMilli?: number | null;
  boundary: "start" | "end";
  timeZone: string;
  value?: string;
}): number | undefined => {
  if (savedUtcMilli != null && input.value === utcMilliToDateInput(savedUtcMilli, input.timeZone)) {
    return savedUtcMilli;
  }
  return dateInputToUtcMilli(input);
};

const savedEventBoundaryToUtcMilli = (event: EventFormState, boundary: "start" | "end", timeZone: string): number => {
  const date = boundary === "start" ? event.startDate : event.endDate;
  const time = boundary === "start" ? event.startTime : event.endTime;
  const saved = boundary === "start" ? event.savedStartAtUtcMilli : event.savedEndAtUtcMilli;
  const dateOnly = event.eventKind === CmpEventKindEnum.Community && !time;
  if (Number.isFinite(saved)) {
    const previous = utcMilliToDateTimeInputParts(saved!, timeZone);
    if (date === previous.date && (dateOnly ? (boundary === "start" ? event.startDateOnly : event.endDateOnly) : time === previous.time)) return saved!;
  }
  const timestamp = dateTimeInputPartsToUtcMilli(date, dateOnly ? boundary === "start" ? "00:00" : "23:59" : time, timeZone);
  return dateOnly && boundary === "end" ? timestamp + 59999 : timestamp;
};

export const eventFormStateToInput = ({
  event,
  timeZone,
}: {
  event: EventFormState;
  timeZone: string;
}): CmpEventDataInput => {
  const normalizedTimeZone = normalizeCmpTimeZone(timeZone).timeZone;
  const locationMode =
    event.inPersonEnabled && event.virtualEnabled
      ? CmpEventLocationModeEnum.Hybrid
      : event.virtualEnabled
        ? CmpEventLocationModeEnum.Virtual
        : CmpEventLocationModeEnum.InPerson;
  // Decision Q15: the event-level sponsorship display flag is always saved as
  // shown; a form that still carries it hidden hides every level instead
  // (eventReturnToFormState already folds saved events this way).
  const sponsorshipsHiddenForEvent = event.sponsorshipPublicDisplayEnabled === false;
  const mappedTickets = event.tickets.map((ticket, index) => ({
        id: ticket.id,
        code: ticket.code || undefined,
        name: ticket.name,
        description: ticket.description || undefined,
        priceInCents: parseCents(ticket.price),
        attendeesPerTicket:
          parseOptionalNumber(ticket.attendeesPerTicket || "1") || 1,
        audience: getTicketAudienceForSave(ticket),
        attendanceMode: ticket.attendanceMode ?? CmpEventAttendanceModeEnum.Either,
        salesStartUtcMilli: savedSalesBoundaryToUtcMilli({
          savedUtcMilli: ticket.savedSalesStartUtcMilli,
          boundary: "start",
          timeZone: normalizedTimeZone,
          value: ticket.salesStartDate,
        }),
        salesEndUtcMilli: savedSalesBoundaryToUtcMilli({
          savedUtcMilli: ticket.savedSalesEndUtcMilli,
          boundary: "end",
          timeZone: normalizedTimeZone,
          value: ticket.salesEndDate,
        }),
        availabilityLimit: ticket.availabilityLimit
          ? parseOptionalNumber(ticket.availabilityLimit)
          : null,
        visibleOnRegistrationForm: isTicketVisibleOnRegistrationForm(ticket),
        // Saved with the ticket so an event save never resets No Tax.
        noTax: ticket.noTax === true,
        sortOrder: index,
      }));
  const tickets = event.eventIsFree ? [buildFreeEventTicketInput(event), ...mappedTickets.filter(ticket =>
    ticket.audience === CmpEventTicketAudienceEnum.SponsorIncluded && ticket.priceInCents === 0 && !ticket.visibleOnRegistrationForm)] : mappedTickets;
  const registrationQuestionCustomFields =
    registrationQuestionsToCustomFieldInputs({
      registrationQuestions: event.registrationQuestions,
    });
  const customFields: CmpEventCustomFieldInput[] = [
    ...registrationQuestionCustomFields,
    ...event.customFields
      .filter(
        (customField) => !isRegistrationQuestionFormCustomField(customField),
      )
      .map((customField, index) => ({
        id: customField.id,
        label: customField.label,
        fieldType:
          customField.backendFieldType || getFieldTypeEnum(customField.type),
        scope: customField.scope || CmpEventCustomFieldScopeEnum.Attendee,
        required: customField.required,
        helperText: customField.helperText || undefined,
        choices: getCustomFieldChoicesForSave(customField),
        appliesToTicketTypeIds: customField.appliesToTicketTypeIds || [],
        appliesToSponsorshipPackageIds:
          customField.appliesToSponsorshipPackageIds || [],
        sortOrder:
          customField.sortOrder ??
          registrationQuestionCustomFields.length + index,
      })),
  ];

  return {
    id: event.id === "new-event" ? undefined : event.id,
    slug: event.slug || undefined,
    eventKind: event.eventKind,
    externalEventUrl: normalizeEventButtonUrl(event.externalEventUrl) || undefined,
    startDateOnly: event.eventKind === CmpEventKindEnum.Community && !event.startTime,
    endDateOnly: event.eventKind === CmpEventKindEnum.Community && !event.endTime,
    publishAtUtcMilli: event.publishAtUtcMilli,
    preCheckInEnabled: event.preCheckInEnabled,
    preCheckInHours: event.preCheckInHours,
    sponsorshipPublicDisplayEnabled: true,
    sponsorshipTaxMode: event.sponsorshipTaxMode,
    title: event.title,
    // The event editor emits a non-breaking space for every space, so save normal spaces.
    description: event.description && withBreakableEmailSpaces(event.description),
    status: getStatusEnum(event.status),
    startAtUtcMilli: savedEventBoundaryToUtcMilli(event, "start", normalizedTimeZone),
    endAtUtcMilli: savedEventBoundaryToUtcMilli(event, "end", normalizedTimeZone),
    timeZone: normalizedTimeZone,
    eventTypeId: event.eventTypeId,
    eventTypeLabelSnapshot: event.eventType,
    eventTypeColorSnapshot: event.eventTypeColor,
    featuredImageUrl: event.featuredBanner || undefined,
    visibility: getVisibilityEnum(event.visibility),
    publicRegistrationEnabled: event.publicRegistrationEnabled,
    eventCode: event.eventCode || undefined,
    heroShortDescription: withBreakableEmailSpaces(event.heroShortDescription || "") || undefined,
    hideHeroTitle: event.hideHeroTitle === true,
    registrationDetailsHtml: withBreakableEmailSpaces(event.registrationDetailsHtml || "") || undefined,
    specialVenueConsiderations: event.specialVenueConsiderations || undefined,
    dressCode: getDressCodeEnum(event.dressCode),
    speakers: resolveEventSpeakerKeynotes(event.speakers || []).map((speaker, index) => ({
      id: speaker.id,
      name: speaker.name,
      company: speaker.company || undefined,
      title: speaker.title || undefined,
      imageUrl: speaker.imageUrl || undefined,
      bio: withBreakableEmailSpaces(speaker.bio || "") || undefined,
      isKeynote: speaker.isKeynote === true,
      sortOrder: index,
    })),
    // Community events do not show partners (design), so none are sent.
    partners:
      event.eventKind === CmpEventKindEnum.Community
        ? []
        : (event.partners || []).map((partner, index) => ({
            id: partner.id,
            logoUrl: partner.logoUrl,
            websiteUrl: normalizeEventButtonUrl(partner.websiteUrl) || undefined,
            sortOrder: index,
          })),
    agendaItems: (event.agendaItems || []).map((agendaItem, index) => ({
      id: agendaItem.id,
      timeLabel: agendaItem.timeLabel,
      details: agendaItem.details,
      sortOrder: index,
    })),
    eventIsFree: event.eventIsFree ?? false,
    displayRemainingTicketQuantity:
      event.displayRemainingTicketQuantity ?? true,
    displayRemainingTicketTypeQuantity:
      event.displayRemainingTicketTypeQuantity ?? false,
    allowWaitlist: event.allowWaitlist ?? false,
    maxAttendeeCapacity: parseOptionalNumber(event.capacity),
    location: {
      mode: locationMode,
      latitude: event.inPersonEnabled ? event.latitude : undefined,
      longitude: event.inPersonEnabled ? event.longitude : undefined,
      venueName: event.inPersonEnabled || event.eventKind === CmpEventKindEnum.Community ? event.venueName : undefined,
      streetAddress: event.inPersonEnabled ? event.address : undefined,
      city: event.inPersonEnabled ? event.city || undefined : undefined,
      province: event.inPersonEnabled ? event.province || undefined : undefined,
      postalCode: event.inPersonEnabled
        ? event.postalCode || undefined
        : undefined,
      country: event.inPersonEnabled ? event.country || undefined : undefined,
      virtualDetails: event.virtualEnabled
        ? event.virtualAccessInstructions
        : undefined,
    },
    tickets,
    customFields,
    registrationQuestionSettingsJson: registrationQuestionsToSettingsJson({
      registrationQuestions: event.registrationQuestions,
    }),
    sponsorshipEnabled: event.sponsorshipEnabled,
    sponsorshipPackages: event.sponsorshipPackages.map(
      (sponsorshipPackage, index) => ({
        id: sponsorshipPackage.id,
        code: sponsorshipPackage.code || undefined,
        name: sponsorshipPackage.name,
        description: sponsorshipPackage.description || undefined,
        priceInCents: parseCents(sponsorshipPackage.price),
        maxAvailable: parseOptionalNumber(sponsorshipPackage.maxAvailable),
        publicDisplayEnabled: sponsorshipsHiddenForEvent ? false : sponsorshipPackage.publicDisplayEnabled,
        websiteListingEnabled: sponsorshipPackage.websiteListingEnabled !== false,
        logoRequired: sponsorshipPackage.logoRequired,
        linkRequired: sponsorshipPackage.linkRequired,
        heroBannerEnabled: sponsorshipPackage.heroBannerEnabled ?? false,
        includedAttendeeQuantity:
          parseOptionalNumber(sponsorshipPackage.includedTickets) || 0,
        includedAttendeeTicketTypeId:
          sponsorshipPackage.includedTicketTypeId || undefined,
        additionalBenefits: withBreakableEmailSpaces(sponsorshipPackage.additionalBenefits || "") || undefined,
        includedTicketValueInCents: parseCents(sponsorshipPackage.includedTicketValue || ""),
        addOns: (sponsorshipPackage.addOns || []).map((addOn) => ({
          id: addOn.id,
          description: addOn.description,
          code: addOn.code || undefined,
          quantity: parseOptionalNumber(addOn.quantity) || 0,
          unitValueInCents: parseCents(addOn.unitValue || ""),
          taxable: addOn.taxable,
        })),
        sortOrder: index,
      }),
    ),
    internalCosts: event.costs.map((cost) => ({
      id: cost.id,
      label: cost.label,
      amountInCents: parseCents(cost.amount),
      note: cost.note,
    })),
    responsibilities: event.responsibilities.map((responsibility) => ({
      id: responsibility.id,
      label: responsibility.task,
      assignedTo: responsibility.owner || undefined,
      note: responsibility.note,
    })),
    notes: [
      ...(event.internalNotes
        ? [{ ...event.savedNotes?.[0], id: event.savedNotes?.[0]?.id || "internal-note",
            body: event.internalNotes, visibility: event.savedNotes?.[0]?.visibility || "STAFF" }]
        : []),
      ...(event.savedNotes?.slice(1) || []),
    ],
  };
};
